import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { getBillingAnalyticsApi, triggerCsvExportApi } from '../api/adminBilling.api';
import { apiHandler, unwrapData } from '../utils/apiHandler';
import PageHeader from '../components/PageHeader';

const PIE_COLORS = ['#10B981', '#3B82F6', '#EC4899', '#F59E0B', '#8B5CF6', '#64748B'];

export default function PaymentAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  
  // CSV Export filters state
  const [exportStatus, setExportStatus] = useState('');
  const [exportType, setExportType] = useState('');
  const [exporting, setExporting] = useState(false);

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    const result = await apiHandler(() => getBillingAnalyticsApi());
    if (result.success) {
      setData(unwrapData(result));
    } else {
      setError(result.message || 'Failed to load billing analytics');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleExportSubmit = async (e) => {
    e.preventDefault();
    setExporting(true);

    const filters = {};
    if (exportStatus) filters.status = exportStatus;
    if (exportType) filters.type = exportType;

    const result = await apiHandler(() => triggerCsvExportApi({ filters }));
    if (result.success) {
      showToast('Export job queued successfully! A SendGrid email with a secure download link will be dispatched shortly.', 'success');
    } else {
      showToast(result.message || 'Failed to queue export job', 'error');
    }
    setExporting(false);
  };

  const planData = (data?.planDistribution || []).filter(p => p.value > 0);
  const paymentPerformance = data?.paymentPerformance || [];

  return (
    <>
      {toast && (
        <div
          className="fixed top-20 right-5 z-[99999] max-w-sm px-5 py-3 rounded-xl shadow-md border text-sm font-semibold animate-slide-in"
          style={{
            background: toast.type === 'success' ? '#F0FDF4' : toast.type === 'error' ? '#FEF2F2' : '#EFF6FF',
            borderColor: toast.type === 'success' ? '#BBF7D0' : toast.type === 'error' ? '#FCA5A5' : '#BFDBFE',
            color: toast.type === 'success' ? '#15803D' : toast.type === 'error' ? '#B91C1C' : '#1D4ED8',
          }}
        >
          {toast.msg}
        </div>
      )}

      <PageHeader
        title="Payment & Subscriptions Analytics"
        icon="fa-chart-pie"
        trail={[{ label: 'Billing' }, { label: 'Analytics' }]}
      />

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mb-4 text-xs text-rose-700 font-medium">
          {error}
        </div>
      )}

      {loading ? (
        <div className="db-skeleton-block h-64 w-full" />
      ) : (
        <div className="flex flex-col gap-6">
          
          {/* Main Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            
            {/* Chart 1: Plan Distribution */}
            <div className="db-card">
              <div className="db-card-header border-b pb-3 mb-4">
                <div className="db-card-title text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <i className="fas fa-chart-pie text-emerald-600" />
                  Subscription Plan Distribution
                </div>
              </div>
              <div className="db-card-body p-4 flex flex-col justify-center min-h-[300px]">
                {planData.length === 0 ? (
                  <div className="db-chart-empty text-slate-400 text-xs text-center">No active billing subscriptions logged</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie
                        data={planData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={85}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {planData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name, props) => [value, props.payload.name]} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Chart 2: Succeeded vs Failed Payments */}
            <div className="db-card">
              <div className="db-card-header border-b pb-3 mb-4">
                <div className="db-card-title text-sm font-bold text-slate-800 flex items-center gap-1.5">
                  <i className="fas fa-chart-column text-sky-800" />
                  Daily Payment Performance (Last 30 Days)
                </div>
              </div>
              <div className="db-card-body p-4 flex flex-col justify-center min-h-[300px]">
                {paymentPerformance.length === 0 ? (
                  <div className="db-chart-empty text-slate-400 text-xs text-center">No transaction trend data found</div>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={paymentPerformance} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                      <XAxis dataKey="date" stroke="#94A3B8" fontSize={9} tickLine={false} />
                      <YAxis stroke="#94A3B8" fontSize={9} tickLine={false} />
                      <Tooltip 
                        contentStyle={{ background: '#0F172A', border: 'none', borderRadius: 8, color: '#fff', fontSize: 11 }}
                        formatter={(value) => [`$${value}`, 'Amount']}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="succeeded" name="Succeeded ($)" fill="#10B981" stackId="a" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="failed" name="Failed ($)" fill="#EF4444" stackId="a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

          </div>

          {/* Export Control Panel (Background Worker Enqueue) */}
          <div className="section-card">
            <div className="section-header border-b pb-3 mb-4">
              <div className="section-title text-sm font-bold text-slate-800 flex items-center gap-1.5">
                <i className="fas fa-download text-amber-600" />
                Background Transaction CSV Export
              </div>
            </div>
            
            <form onSubmit={handleExportSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Payment Status</label>
                <select 
                  className="filter-select w-full border border-slate-200 rounded-lg p-2.5 text-xs text-slate-700 bg-white"
                  value={exportStatus}
                  onChange={(e) => setExportStatus(e.target.value)}
                >
                  <option value="">Any Status (Succeeded, Failed, Refunded)</option>
                  <option value="succeeded">Succeeded Only</option>
                  <option value="failed">Failed Only</option>
                  <option value="refunded">Refunded Only</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Transaction Type</label>
                <select 
                  className="filter-select w-full border border-slate-200 rounded-lg p-2.5 text-xs text-slate-700 bg-white"
                  value={exportType}
                  onChange={(e) => setExportType(e.target.value)}
                >
                  <option value="">Any Type (Subscription, Adjustment, Refund)</option>
                  <option value="subscription">Subscription Payments</option>
                  <option value="refund">Refunds</option>
                  <option value="adjustment">Adjustments</option>
                </select>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={exporting}
                  className="action-btn btn-orange w-full justify-center py-2.5 text-xs font-bold flex items-center gap-1.5 rounded-lg transition-all"
                >
                  {exporting ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Enqueuing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane" /> Dispatch Export Request
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="mt-4 text-slate-400 text-[10px] flex items-center gap-1.5">
              <i className="fas fa-circle-info" />
              <span>
                Exporting triggers a background processing worker that streams all matched table rows, generates a secure download link, and emails it to your admin account using SendGrid.
              </span>
            </div>
          </div>

        </div>
      )}
    </>
  );
}
